import React, { useState } from "react";
import { Send, Check, Building2 } from "lucide-react";
import { api } from "@/lib/api";

export default function PartnerWithUs() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    brand_name: "",
    cuisine: "",
    message: "",
  });
  const [status, setStatus] = useState("idle"); // idle | sending | sent | error
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setStatus("sending");
    setError("");
    try {
      await api.post("/partner-enquiries", form);
      setStatus("sent");
      setForm({ name: "", email: "", phone: "", brand_name: "", cuisine: "", message: "" });
    } catch (err) {
      setStatus("error");
      setError(err.response?.data?.detail || err.message || "Something went wrong.");
    }
  };

  const onChange = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <section
      id="partner"
      className="py-20 lg:py-28 bg-[var(--surface)] border-y border-[var(--border)]"
      data-testid="partner-section"
    >
      <div className="container-nk">
        <div className="grid lg:grid-cols-2 gap-14 lg:gap-20 items-start">
          {/* Left: pitch */}
          <div>
            <p className="eyebrow flex items-center gap-2">
              <Building2 size={13} className="text-gold" />
              // For Food Operators
            </p>
            <h2 className="font-display text-[56px] md:text-[80px] leading-[0.95] mt-3">
              Partner<br />
              <span className="text-gold">With Us.</span>
            </h2>
            <p className="mt-7 text-[15.5px] leading-[1.75] text-[var(--text)]/80 font-light max-w-md">
              Got a food brand the city needs to taste? Move into one of our
              twenty state-of-the-art kitchens — share our infrastructure, our
              delivery-platform integrations, and our footfall. You bring the
              recipes; we handle the rest.
            </p>

            <ul className="mt-8 flex flex-col gap-3.5 max-w-md">
              {[
                "All 4 major delivery platforms wired up day one",
                "Listed on this hub site and in our walk-in counter",
                "Flexible monthly terms · no long-term lock-in",
              ].map((b) => (
                <li key={b} className="flex items-start gap-3 text-[14px] text-[var(--text)]/85">
                  <Check size={16} className="text-gold mt-0.5 flex-shrink-0" />
                  <span>{b}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Right: form */}
          <div className="bg-[var(--surface-elevated)] border border-[var(--border)] rounded-2xl p-8 lg:p-10">
            {status === "sent" ? (
              <div className="flex flex-col items-center text-center py-8" data-testid="partner-success">
                <div className="w-14 h-14 rounded-full bg-[var(--gold)] flex items-center justify-center mb-5">
                  <Check size={24} className="text-[var(--bg)]" />
                </div>
                <h3 className="font-display text-[36px] leading-none">Got it.</h3>
                <p className="text-muted text-[14px] mt-3 max-w-xs">
                  Thanks for reaching out — we'll be in touch within 48 hours.
                </p>
                <button
                  className="btn-ghost mt-7"
                  onClick={() => setStatus("idle")}
                  data-testid="partner-send-another"
                >
                  Send another
                </button>
              </div>
            ) : (
              <form onSubmit={submit} className="flex flex-col gap-4" data-testid="partner-form">
                <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-gold mb-1">
                  Tell us about your brand
                </p>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block font-mono text-[10px] uppercase tracking-[0.14em] text-muted mb-1.5">
                      Your name *
                    </label>
                    <input required value={form.name} onChange={onChange("name")} className="input-nk" data-testid="partner-name" />
                  </div>
                  <div>
                    <label className="block font-mono text-[10px] uppercase tracking-[0.14em] text-muted mb-1.5">
                      Email *
                    </label>
                    <input type="email" required value={form.email} onChange={onChange("email")} className="input-nk" data-testid="partner-email" />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block font-mono text-[10px] uppercase tracking-[0.14em] text-muted mb-1.5">
                      Phone
                    </label>
                    <input value={form.phone} onChange={onChange("phone")} className="input-nk" data-testid="partner-phone" />
                  </div>
                  <div>
                    <label className="block font-mono text-[10px] uppercase tracking-[0.14em] text-muted mb-1.5">
                      Brand name *
                    </label>
                    <input required value={form.brand_name} onChange={onChange("brand_name")} className="input-nk" data-testid="partner-brand" />
                  </div>
                </div>

                <div>
                  <label className="block font-mono text-[10px] uppercase tracking-[0.14em] text-muted mb-1.5">
                    Cuisine / concept
                  </label>
                  <input value={form.cuisine} onChange={onChange("cuisine")} className="input-nk" placeholder="e.g. Korean BBQ, Vegan Tacos, Lebanese Mezze" data-testid="partner-cuisine" />
                </div>

                <div>
                  <label className="block font-mono text-[10px] uppercase tracking-[0.14em] text-muted mb-1.5">
                    Tell us more *
                  </label>
                  <textarea required rows={4} value={form.message} onChange={onChange("message")} className="input-nk" placeholder="Where are you trading now? Why Naami?" data-testid="partner-message" />
                </div>

                {error && <p className="text-[var(--error)] text-[13px]" data-testid="partner-error">{error}</p>}

                <button
                  type="submit"
                  disabled={status === "sending"}
                  className="btn-primary justify-center mt-2"
                  data-testid="partner-submit"
                >
                  {status === "sending" ? "Sending…" : (<>Send Enquiry <Send size={13} /></>)}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
